import React, { useState, useEffect } from "react";
import PublicNavbar from "../components/PublicNavbar";
import { API_URL } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ShoppingCart, Plus } from "lucide-react";

// Fallback items when API returns empty
const FALLBACK_SHOP_ITEMS = [
  { item_id: "1", name: "Generic Black Essentials Pickleball Paddle", available_quantity: 20, price: 3695.00, image: "/newassets/11.png" },
  { item_id: "2", name: "Portable Pickleball Net", available_quantity: 12, price: 1800.00, image: "/newassets/22.png" },
  { item_id: "3", name: "Pro Hybrid+ Pickleball", available_quantity: 30, price: 325.00, image: "/newassets/33.png" },
  { item_id: "4", name: "Ball (3pcs.)", available_quantity: 50, price: 100.00, image: "/newassets/44.png" },
];

export default function Shop({ embedded = false }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [shopItems, setShopItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const itemsPerPage = embedded ? 4 : 8;
  
  const { addToCart, isLoggedIn } = useCart();

  useEffect(() => {
    fetchShopItems();
  }, []);

  const fetchShopItems = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/addons`);
      if (res.ok) {
        const data = await res.json();
        if (data && data.length > 0) {
          setShopItems(data);
          // Extract unique categories
          const uniqueCategories = [...new Set(data.map(item => item.category).filter(Boolean))];
          setCategories(uniqueCategories);
        } else {
          setShopItems(FALLBACK_SHOP_ITEMS);
        }
      } else {
        setShopItems(FALLBACK_SHOP_ITEMS);
      }
    } catch (error) {
      console.error("Failed to fetch shop items:", error);
      setShopItems(FALLBACK_SHOP_ITEMS);
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on search and category
  const filteredItems = shopItems.filter(item => {
    const matchesSearch = item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          item.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Pagination
  const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
  const paginatedItems = filteredItems.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory]);

  return (
    <div className={`bg-white font-sans ${embedded ? 'py-10' : 'pb-20'}`}>
      {!embedded && <PublicNavbar />}

      {/* Header Badge */}
      <div className={`flex justify-center ${embedded ? 'mb-8' : 'mt-10 mb-12'} px-4`}>
        <div className="relative inline-block">
          <div className="absolute inset-0 bg-[#c5e153] translate-x-[-4px] translate-y-[6px] rounded-2xl md:rounded-[2rem]" />
          <div className="relative flex items-center gap-3 bg-[#004a80] text-white px-8 py-3 md:px-12 md:py-4 rounded-2xl md:rounded-[2rem] shadow-sm">
            <div className="flex-shrink-0">
              <img src="/newassets/icon 5.png" alt="Shop" className="w-6 h-6 md:w-8 md:h-8 object-contain" />
            </div>
            <span className="font-extrabold text-xl md:text-3xl tracking-tight uppercase">Shop</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6">
        {/* Search and Filter Bar */}
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6 mb-16 w-full">
        
        {/* Search Input */}
        <div className="relative w-full lg:max-w-md">
            <input
            type="text"
            placeholder="Search products..."
            className="w-full pl-6 pr-12 py-3 rounded-full border-2 border-gray-200 focus:border-[#c5e153] outline-none text-gray-700 font-medium shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button className="absolute right-4 top-1/2 -translate-y-1/2">
            <img 
                src="/newassets/Search.png" 
                alt="Search" 
                className="w-6 h-6 -scale-x-100" 
            />
            </button>
        </div>

        {/* Filter and Sort */}
        <div className="flex items-center gap-4 w-full lg:w-auto justify-center flex-wrap">
            {/* Category Filter Dropdown */}
            {categories.length > 0 && (
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-3 rounded-full border-2 border-gray-200 bg-white text-gray-700 font-medium focus:border-[#c5e153] outline-none"
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            )}

            <div className="flex items-center gap-3">
            {/* Latest Arrivals Pill */}
            <button className="bg-[#ff8a00] text-white px-6 sm:px-10 py-3 rounded-full font-bold uppercase text-xs sm:text-sm tracking-wide shadow-sm hover:bg-[#e67e00] transition-colors whitespace-nowrap">
                Latest Arrivals
            </button>
            </div>
        </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004a80]"></div>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No products found</p>
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="mt-4 text-[#004a80] font-medium hover:underline"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
        <>
        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {paginatedItems.map((item) => (
                <div 
                key={item.item_id || item.id} 
                className="group relative bg-white rounded-[2.5rem] shadow-[0_10px_30px_rgba(0,0,0,0.08)] overflow-hidden transition-transform hover:-translate-y-2 flex flex-col h-[60vh] lg:h-[50vh]"
                >
                {/* Image Container */}
                <div className="flex-grow w-full overflow-hidden bg-gray-100 flex items-center justify-center">
                    {item.image ? (
                      <img 
                        src={item.image} 
                        alt={item.name} 
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/newassets/placeholder.png";
                        }}
                      />
                    ) : (
                      <div className="text-gray-400 text-center p-4">
                        <svg className="w-16 h-16 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span className="text-sm">No Image</span>
                      </div>
                    )}
                </div>

                {/* Lime Green Info Section */}
                <div className="bg-[#c5e153] p-6 h-[160px] flex flex-col justify-between w-full shrink-0">
                    <div>
                    <h4 className="text-[#004a80] font-black text-sm leading-tight mb-1 uppercase line-clamp-2">
                        {item.name}
                    </h4>
                    <p className="text-[#004a80] text-[11px] font-bold">
                        {item.category === 'Services' ? 'Service' : `(${item.available_quantity || item.stock || 0}) in stock`}
                    </p>
                    </div>
                    
                    <div className="flex justify-between items-end">
                    <p className="text-[#004a80] font-black text-lg">
                        ₱ {(item.price || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </p>
                    <button 
                      onClick={() => addToCart(item)}
                      className="bg-[#004a80] text-white w-10 h-10 rounded-full flex items-center justify-center shadow-md hover:bg-[#003a60] transition-all active:scale-95 group"
                      title={isLoggedIn ? "Add to cart" : "Log in to add to cart"}
                    >
                        <ShoppingCart className="h-5 w-5 group-hover:hidden" />
                        <Plus className="h-5 w-5 hidden group-hover:block" />
                    </button>
                    </div>
                </div>
                </div>
            ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
        <div className="flex justify-center items-center gap-3">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className={`w-8 h-8 flex items-center justify-center text-[#ff8a00] font-bold text-xl ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-50 rounded-full'}`}
          >
            ‹
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).slice(
            Math.max(0, currentPage - 3),
            Math.min(totalPages, currentPage + 2)
          ).map((num) => (
            <button
              key={num}
              onClick={() => setCurrentPage(num)}
              className={`w-8 h-8 rounded-full flex items-center justify-center font-black text-sm transition-colors ${
                currentPage === num 
                ? "bg-white border-2 border-[#ff8a00] text-[#ff8a00]" 
                : "bg-[#ff8a00] text-white hover:bg-[#e67e00]"
              }`}
            >
              {num}
            </button>
          ))}
          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className={`w-8 h-8 flex items-center justify-center text-[#ff8a00] font-bold text-xl ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-50 rounded-full'}`}
          >
            ›
          </button>
        </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}